import React, { useState, useEffect, useMemo } from "react";
import { Form, Button, Upload, Row, Col, Input, Card, message, Radio } from "antd";
import { UploadOutlined, PlusOutlined, MinusCircleOutlined, FileOutlined } from "@ant-design/icons";
import axios from "axios";
import { FormStepWrapper } from "./AddTalentProfile.style";
import { API_CONST } from "../../../../const";

const AdditionalInformation = ({ initialData, onBack, onSubmit, onCancel, isLastStep, isEditMode }) => {
  const [form] = Form.useForm();

  // Stable ID for this form session: real talent ID in edit mode,
  // single TEMP token generated once at mount for new talent creation.
  const sessionId = useMemo(() => `TEMP-${Date.now()}`, []);
  const odataId = initialData.id || sessionId;

  const [resume, setResume] = useState(initialData.resume || null);
  const [idProof, setIdProof] = useState(initialData.idProof || null);
  const [idProofType, setIdProofType] = useState(initialData.idProofType || 'aadhaar');
  const [pan, setPan] = useState(initialData.pan || null);
  const [degree, setDegree] = useState(initialData.degree || null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initialData && initialData.summary !== undefined) {
      form.setFieldsValue(initialData);
    }
  }, [initialData, form]);

  const uploadFileToStorage = async (file, documentType) => {
    const token = localStorage.getItem("authToken");
    const userName = initialData.name || "Unknown";
    
    const presignedResponse = await axios.post(
      API_CONST.UPLOAD_REQUEST_URL,
      {
        name: file.name,
        userName: userName,
        odataId: odataId,
        size: file.size,
        contentType: file.type,
        documentType: documentType,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const { uploadURL, objectPath } = presignedResponse.data;

    await fetch(uploadURL, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    return objectPath;
  };

  const uploadAllDocuments = async () => {
    const uploadResults = {
      resumeUrl: null,
      aadhaarUrl: null,
      passportUrl: null,
      panUrl: null,
      degreeUrl: null,
    };

    try {
      const uploadPromises = [];

      if (resume) {
        uploadPromises.push(
          uploadFileToStorage(resume, 'resume')
            .then(url => { uploadResults.resumeUrl = url; })
        );
      }

      if (idProof) {
        uploadPromises.push(
          uploadFileToStorage(idProof, idProofType)
            .then(url => { 
              if (idProofType === 'aadhaar') {
                uploadResults.aadhaarUrl = url;
              } else {
                uploadResults.passportUrl = url;
              }
            })
        );
      }

      if (pan) {
        uploadPromises.push(
          uploadFileToStorage(pan, 'pan')
            .then(url => { uploadResults.panUrl = url; })
        );
      }

      if (degree) {
        uploadPromises.push(
          uploadFileToStorage(degree, 'degree')
            .then(url => { uploadResults.degreeUrl = url; })
        );
      }

      if (uploadPromises.length > 0) {
        await Promise.all(uploadPromises);
      }

      return uploadResults;
    } catch (error) {
      console.error("Error uploading documents:", error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      setSubmitting(true);
      message.loading({ content: 'Uploading documents...', key: 'upload', duration: 0 });

      let documentUrls = {
        resumeUrl: initialData.resumeUrl || null,
        aadhaarUrl: initialData.aadhaarUrl || null,
        passportUrl: initialData.passportUrl || null,
        panUrl: initialData.panUrl || null,
        degreeUrl: initialData.degreeUrl || null,
      };

      if (resume || idProof || pan || degree) {
        documentUrls = await uploadAllDocuments();
        message.success({ content: 'Documents uploaded successfully!', key: 'upload', duration: 2 });
      } else {
        message.destroy('upload');
      }

      const finalData = {
        ...values,
        ...documentUrls,
      };

      onSubmit(finalData);
    } catch (error) {
      console.error("Submit error:", error);
      message.error({ content: 'Failed to upload documents. Please try again.', key: 'upload', duration: 3 });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    const formValues = form.getFieldsValue();
    const documentData = {
      ...formValues,
      resume,
      idProof,
      idProofType,
      pan,
      degree,
    };
    onBack(documentData);
  };

  const renderFileInfo = (file, onRemove) => {
    if (!file) return null;
    return (
      <div className="file-info" style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        <FileOutlined style={{ color: '#52c41a' }} />
        <span className="file-name" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.name}</span>
        <span 
          className="remove-btn" 
          onClick={onRemove}
          style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: 12 }}
        >
          Remove
        </span>
      </div>
    );
  };

  return (
    <FormStepWrapper>
      <h2 className="form-title">Additional Information</h2>
      
      <Form 
        form={form} 
        layout="vertical"
        initialValues={initialData}
      >
        <div className="upload-section" style={{ marginBottom: 24 }}>
          <label className="upload-label">Upload Resume *</label>
          <Upload
            beforeUpload={(file) => {
              setResume(file);
              return false;
            }}
            showUploadList={false}
            accept=".pdf,.doc,.docx"
            disabled={submitting}
          >
            <Button icon={<UploadOutlined />} disabled={submitting}>
              {resume ? "Change Resume" : "Click to Upload Resume"}
            </Button>
          </Upload>
          {renderFileInfo(resume, () => setResume(null))}
        </div>

        <Form.Item label="Professional Summary (Optional)" name="summary">
          <Input.TextArea 
            rows={4} 
            placeholder="Enter professional summary highlighting key skills, experience, and expertise..." 
            disabled={submitting}
          />
        </Form.Item>
        
        <Form.Item label="Project Experience (Optional)">
          <Form.List name="projects">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card
                    key={key}
                    size="small"
                    style={{ marginBottom: 16, background: '#fafafa' }}
                    extra={
                      <MinusCircleOutlined
                        onClick={() => !submitting && remove(name)}
                        style={{ color: submitting ? '#d9d9d9' : '#ff4d4f', fontSize: '16px', cursor: submitting ? 'not-allowed' : 'pointer' }}
                      />
                    }
                  >
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'title']}
                          label="Project Title"
                          rules={[{ required: true, message: 'Please enter project title' }]}
                        >
                          <Input placeholder="e.g., E-commerce Platform Development" disabled={submitting} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'client']}
                          label="Client"
                        >
                          <Input placeholder="e.g., Fortune 500 Company" disabled={submitting} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, 'duration']}
                          label="Duration"
                        >
                          <Input placeholder="e.g., 6 months" disabled={submitting} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, 'role']}
                          label="Your Role"
                        >
                          <Input placeholder="e.g., Lead Developer" disabled={submitting} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, 'technologies']}
                          label="Technologies"
                        >
                          <Input placeholder="e.g., React, Node.js, MongoDB" disabled={submitting} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item
                      {...restField}
                      name={[name, 'description']}
                      label="Description"
                    >
                      <Input.TextArea
                        rows={3}
                        placeholder="Describe the project, your contributions, and achievements..."
                        disabled={submitting}
                      />
                    </Form.Item>
                  </Card>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    disabled={submitting}
                  >
                    Add Project
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <div className="upload-section">
              <label className="upload-label">Upload ID Proof (Aadhaar / Passport)</label>
              <Radio.Group 
                value={idProofType} 
                onChange={(e) => setIdProofType(e.target.value)}
                disabled={submitting}
                style={{ marginBottom: 8, display: 'block' }}
              >
                <Radio value="aadhaar">Aadhaar Card</Radio>
                <Radio value="passport">Passport</Radio>
              </Radio.Group>
              <Upload
                beforeUpload={(file) => {
                  setIdProof(file);
                  return false;
                }}
                showUploadList={false}
                accept=".pdf,.jpg,.jpeg,.png"
                disabled={submitting}
              >
                <Button icon={<UploadOutlined />} disabled={submitting}>
                  {idProof ? `Change ${idProofType === 'aadhaar' ? 'Aadhaar' : 'Passport'}` : `Upload ${idProofType === 'aadhaar' ? 'Aadhaar' : 'Passport'}`}
                </Button>
              </Upload>
              {renderFileInfo(idProof, () => setIdProof(null))}
            </div>
          </Col>

          <Col span={8}>
            <div className="upload-section">
              <label className="upload-label">Upload PAN Card</label>
              <Upload
                beforeUpload={(file) => {
                  setPan(file);
                  return false;
                }}
                showUploadList={false}
                accept=".pdf,.jpg,.jpeg,.png"
                disabled={submitting}
              >
                <Button icon={<UploadOutlined />} disabled={submitting} style={{ marginTop: 30 }}>
                  {pan ? "Change PAN" : "Upload PAN"}
                </Button>
              </Upload>
              {renderFileInfo(pan, () => setPan(null))}
            </div>
          </Col>

          <Col span={8}>
            <div className="upload-section">
              <label className="upload-label">Upload Degree Proof</label>
              <Upload
                beforeUpload={(file) => {
                  setDegree(file);
                  return false;
                }}
                showUploadList={false}
                accept=".pdf,.jpg,.jpeg,.png"
                disabled={submitting}
              >
                <Button icon={<UploadOutlined />} disabled={submitting} style={{ marginTop: 30 }}>
                  {degree ? "Change Degree" : "Upload Degree"}
                </Button>
              </Upload>
              {renderFileInfo(degree, () => setDegree(null))}
            </div>
          </Col>
        </Row>

        <div style={{ marginTop: 16, padding: 16, background: "#f8f9fd", borderRadius: 4 }}>
          <p style={{ margin: 0, color: "#666", fontSize: 14 }}>
            <strong>Note:</strong> Resume upload is mandatory. Documents will be uploaded when you click Submit.
          </p>
        </div>

        <div className="button-group">
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleBack} disabled={submitting}>
              Back
            </Button>
          </div>
          <Button 
            type="primary" 
            onClick={handleSubmit}
            disabled={(!resume && !isEditMode) || submitting}
            loading={submitting}
            style={{ backgroundColor: "#00d9a9", borderColor: "#00d9a9" }}
          >
            {submitting ? "Uploading & Submitting..." : isEditMode ? "Save Changes" : "Submit"}
          </Button>
        </div>
      </Form>
    </FormStepWrapper>
  );
};

export default AdditionalInformation;
